/**
 * Tracking Service - Escuela de Consciencia Akáshica
 * ==================================================
 * 
 * Sistema de tracking first-party con:
 * - Cookie anonymous_id persistente
 * - Captura de UTM parameters
 * - Page view tracking
 * - Event tracking
 * - Merge anónimo → contacto en formularios
 * 
 * GDPR Compliant: Solo tracking funcional, no third-party
 */

import api from './api';

// Cookie name for anonymous visitor ID
const ANON_ID_COOKIE = 'akashica_vid';
const ANON_ID_EXPIRY_DAYS = 365;

// UTM parameters to capture
const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

/**
 * Generate a unique visitor ID
 */
const generateVisitorId = () => {
  return 'vid_' + Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
};

/**
 * Get or create anonymous visitor ID from cookie
 */
export const getAnonymousId = () => {
  // Try to get existing cookie
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === ANON_ID_COOKIE && value) {
      return value;
    }
  }
  
  // Create new ID and set cookie
  const newId = generateVisitorId();
  const expiry = new Date();
  expiry.setDate(expiry.getDate() + ANON_ID_EXPIRY_DAYS);
  document.cookie = `${ANON_ID_COOKIE}=${newId}; expires=${expiry.toUTCString()}; path=/; SameSite=Lax`;
  
  return newId;
};

/**
 * Get UTM parameters from URL
 */
export const getUTMParams = () => {
  const params = new URLSearchParams(window.location.search);
  const utm = {};
  
  UTM_PARAMS.forEach(param => {
    const value = params.get(param);
    if (value) {
      utm[param] = value;
    }
  });
  
  return utm;
};

/**
 * Get stored UTM params from sessionStorage (persists during session)
 */
export const getStoredUTM = () => {
  try {
    const stored = sessionStorage.getItem('akashica_utm');
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

/**
 * Store UTM params in sessionStorage
 */
const storeUTM = (utm) => {
  try {
    const existing = getStoredUTM();
    // Only update if we have new UTM params (first touch preserved)
    if (Object.keys(utm).length > 0) {
      sessionStorage.setItem('akashica_utm', JSON.stringify({ ...existing, ...utm }));
    }
  } catch {
    // sessionStorage not available
  }
};

/**
 * Get referrer info
 */
const getReferrer = () => {
  const referrer = document.referrer;
  if (!referrer) return null;
  
  try {
    const url = new URL(referrer);
    // Don't track internal referrers
    if (url.hostname === window.location.hostname) return null;
    return referrer;
  } catch {
    return referrer;
  }
};

/**
 * Track an event
 * @param {string} eventName - Name of the event
 * @param {object} properties - Additional properties
 * @param {string} contactId - Optional contact ID if known
 */
export const trackEvent = async (eventName, properties = {}, contactId = null) => {
  try {
    const anonymousId = getAnonymousId();
    const utm = { ...getStoredUTM(), ...getUTMParams() };
    const referrer = getReferrer();
    
    // Store UTM for this session
    storeUTM(utm);
    
    const eventData = {
      event_name: eventName,
      anonymous_id: anonymousId,
      properties: {
        url: window.location.href,
        path: window.location.pathname,
        referrer: referrer,
        title: document.title,
        ...utm,
        ...properties
      }
    };
    
    // Add contact_id to properties if provided
    if (contactId) {
      eventData.properties.contact_id = contactId;
    }
    
    // Send to backend (fire and forget, don't block UI)
    await api.post('/marketing/track', eventData);
    
    return true;
  } catch (error) {
    console.warn('Tracking error:', error);
    return false;
  }
};

/**
 * Track a page view
 * Called automatically on route changes
 */
export const trackPageView = (path = null) => {
  const pagePath = path || window.location.pathname;
  
  // Determine landing type from path
  let landingSlug = 'other';
  if (pagePath === '/') landingSlug = 'home';
  else if (pagePath.includes('/formacion/facilitadoras')) landingSlug = 'facilitadoras';
  else if (pagePath.includes('/formacion/limites')) landingSlug = 'limites';
  else if (pagePath.includes('/formaciones')) landingSlug = 'formaciones';
  else if (pagePath.includes('/campus')) landingSlug = 'campus';
  else if (pagePath.includes('/viajes')) landingSlug = 'viajes';
  else if (pagePath.includes('/quien-soy')) landingSlug = 'quien-soy';
  else if (pagePath.includes('/metodologia')) landingSlug = 'metodologia';
  else if (pagePath.includes('/publicaciones')) landingSlug = 'publicaciones';
  else if (pagePath.includes('/contacto')) landingSlug = 'contacto';
  else if (pagePath.includes('/faq')) landingSlug = 'faq';
  
  trackEvent('page_view', {
    landing_slug: landingSlug,
    page_path: pagePath
  });
};

/**
 * Track form submission with email identification
 * This merges the anonymous visitor with a contact
 * @param {string} formType - Type of form (ebook, contact, waitlist, etc.)
 * @param {string} email - User's email
 * @param {object} additionalData - Any additional form data
 */
export const trackFormSubmit = async (formType, email, additionalData = {}) => {
  const anonymousId = getAnonymousId();
  const utm = { ...getStoredUTM(), ...getUTMParams() };
  
  return trackEvent('form_submit', {
    form_type: formType,
    email: email.toLowerCase(),
    landing_slug: additionalData.landing_slug || window.location.pathname,
    ...utm,
    ...additionalData
  });
};

/**
 * Track lead confirmation (after form submit + email verified or consent given)
 * @param {string} email - User's email
 * @param {string} source - Lead source (ebook, waitlist, etc.)
 */
export const trackLeadConfirmed = async (email, source, additionalData = {}) => {
  const anonymousId = getAnonymousId();
  const utm = { ...getStoredUTM(), ...getUTMParams() };
  
  return trackEvent('lead_confirmed', {
    email: email.toLowerCase(),
    source: source,
    ...utm,
    ...additionalData
  });
};

/**
 * Track button/link click
 * @param {string} elementId - ID or name of the element
 * @param {string} destination - Where the click leads to
 */
export const trackClick = (elementId, destination = null) => {
  trackEvent('click', {
    element_id: elementId,
    destination: destination
  });
};

/**
 * Initialize tracking on app load
 * Call this once in App.js or index.js
 */
export const initTracking = () => {
  // Get/create anonymous ID
  const anonymousId = getAnonymousId();
  
  // Capture initial UTM params
  const utm = getUTMParams();
  if (Object.keys(utm).length > 0) {
    storeUTM(utm);
  }
  
  // Track initial page view
  trackPageView();
  
  console.log('[Tracking] Initialized with visitor ID:', anonymousId);
  
  return anonymousId;
};

/**
 * Get all tracking context for form submissions
 * Use this to enrich form data before sending
 */
export const getTrackingContext = () => {
  return {
    anonymous_id: getAnonymousId(),
    utm: getStoredUTM(),
    referrer: getReferrer(),
    landing_page: window.location.pathname
  };
};

export default {
  getAnonymousId,
  getUTMParams,
  getStoredUTM,
  trackEvent,
  trackPageView,
  trackFormSubmit,
  trackLeadConfirmed,
  trackClick,
  initTracking,
  getTrackingContext
};
